import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { getError } from '../utils';
import Button from 'react-bootstrap/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Container, OverlayTrigger, Popover, Form } from 'react-bootstrap';
import Pagination from '../components/general/Pagination';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        users: action.payload.users,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false };
    case 'DELETE_RESET':
      return { ...state, loadingDelete: false, successDelete: false };

    default:
      return state;
  }
};

export default function UserListScreen() {
  const navigate = useNavigate();
  const [
    { loading, error, users, loadingDelete, successDelete, pages },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present

  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await api.get(
          `/api/users?page=${page}&search=${encodeURIComponent(searchQuery)}`
        );

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };

    if (successDelete) {
      dispatch({ type: 'DELETE_RESET' });
    } else {
      fetchData();
    }
  }, [successDelete, page, searchQuery]);

  const deleteHandler = async (user) => {
    if (window.confirm('Möchten Sie dieses Kundenkonto wirklich löschen?')) {
      try {
        dispatch({ type: 'DELETE_REQUEST' });
        await api.delete(`/api/users/${user.userId}`);
        toast.success('Kundenkonto gelöscht');
        dispatch({ type: 'DELETE_SUCCESS' });
      } catch (error) {
        const errorMessage = getError(error);
        if (errorMessage.includes('nice try \u{1F602}')) {
          toast.error(errorMessage, {
            className: 'nice-try-toast',
          });
        } else {
          toast.error(errorMessage);
        }
        dispatch({
          type: 'DELETE_FAIL',
        });
      }
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.replace(/\s+/g, ''); // Remove spaces
    navigate(
      sanitizedInput
        ? `/admin/users?page=1&search=${encodeURIComponent(sanitizedInput)}`
        : `/admin/users?page=1`
    ); // Navigates to the UserListScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  return (
    <Container>
      <div>
        <title>Benutzer</title>

        <h1 className="screen-heading">Benutzer</h1>

        {/* Search Form */}
        <Form onSubmit={submitHandler} className="d-flex my-5">
          <Form.Control
            type="text"
            placeholder="Search by username, email or userId"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search by username, email or userId"
          />
          <Button type="submit" variant="secondary" className="mx-3">
            Search
          </Button>
        </Form>

        {loading || loadingDelete ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Row className="d-xl-none">
              {users.map((user) => (
                <Col key={user.userId} xs={12} sm={12} md={12} lg={6}>
                  <Card className="products-card">
                    <Card.Body className="text-center">
                      <Card.Title className="code mb-4">
                        {user.userId}
                      </Card.Title>
                      <div className="divider"></div>
                      <div className="code-info">
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Username:
                          </strong>{' '}
                          <span className="no-wrap">{user.username}</span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Email:
                          </strong>{' '}
                          <span className="no-wrap">{user.email}</span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Verified:
                          </strong>{' '}
                          <span className="no-wrap">
                            {user.isVerified ? 'JA' : 'NEIN'}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Admin:
                          </strong>{' '}
                          <span className="no-wrap">
                            {user.isAdmin ? 'JA' : 'NEIN'}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Aktionen:
                          </strong>
                          <span className="no-wrap w-100 mt-1">
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() =>
                                navigate(`/admin/user/${user.userId}`)
                              }
                            >
                              Bearbeiten
                            </Button>
                            &nbsp;
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() => deleteHandler(user)}
                            >
                              Löschen
                            </Button>
                            <OverlayTrigger
                              trigger="click"
                              rootClose // close the popover when the user clicks outside of it
                              placement="bottom"
                              overlay={
                                <Popover>
                                  <Popover.Body>
                                    <strong>Delete</strong> <br />
                                    Deleting a user does not remove the user
                                    document from the database. Instead, all
                                    personal information within the document is
                                    anonymized. This ensures that any related
                                    references in other documents remain valid
                                    and functional, maintaining data integrity
                                    across the system. <br />
                                    <br />
                                    Also the users designs will be deleted. The
                                    designs that have likes or have been
                                    purchased will be soft-deleted. All other
                                    designs will be hard-deleted.
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <Button
                                className="info-popover"
                                style={{ padding: '0px 0px 0px 5px' }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="#707070"
                                  className="bi bi-info-circle"
                                  viewBox="0 0 16 16"
                                  style={{ marginLeft: '5px' }}
                                >
                                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                              </Button>
                            </OverlayTrigger>
                          </span>
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <div className="d-none d-xl-block">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>USERNAME</th>
                    <th>EMAIL</th>
                    <th>Verified</th>
                    {/* <th>Customer Support</th> */}
                    <th>ADMIN</th>
                    <th>
                      <div className="d-flex">
                        AKTIONEN
                        <OverlayTrigger
                          trigger="click"
                          rootClose // close the popover when the user clicks outside of it
                          placement="bottom"
                          overlay={
                            <Popover>
                              <Popover.Body>
                                <strong>Delete</strong> <br />
                                Deleting a user does not remove the user
                                document from the database. Instead, all
                                personal information within the document is
                                anonymized. This ensures that any related
                                references in other documents remain valid and
                                functional, maintaining data integrity across
                                the system. <br />
                                <br />
                                Also the users designs will be deleted. The
                                designs that have likes or have been purchased
                                will be soft-deleted. All other designs will be
                                hard-deleted.
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <Button
                            className="info-popover"
                            style={{ padding: '0px 0px 0px 5px' }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="#707070"
                              className="bi bi-info-circle"
                              viewBox="0 0 16 16"
                              style={{ marginLeft: '5px' }}
                            >
                              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                            </svg>
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.userId}>
                      <td>{user.userId}</td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{user.isVerified ? 'JA' : 'NEIN'}</td>
                      {/* <td>{user.isCustomerSupportAgent ? 'JA' : 'NEIN'}</td> */}
                      <td>{user.isAdmin ? 'JA' : 'NEIN'}</td>
                      <td>
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => navigate(`/admin/user/${user.userId}`)}
                        >
                          Bearbeiten
                        </Button>
                        &nbsp;
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => deleteHandler(user)}
                        >
                          Löschen
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              pages={pages}
              page={Number(page)}
              baseUrl="/admin/users"
              search={searchQuery}
            />
          </>
        )}
      </div>
    </Container>
  );
}
